// `/admin/bug-reports`: fila dos reportes de bug enviados pelos sellers. Rota-PAI, como a de
// KYC: desenha a fila e, por cima, o `RouteDrawer` com o `<Outlet/>` da rota `$id`. Deep link
// em `/admin/bug-reports/bug_...` monta a fila embaixo e o drawer aberto.
//
// Sem step-up: leitura e mudança de status não estão em `ADMIN_STEP_UP_ROUTES`.

import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { adminBugReportsQueryOptions } from '../api/queries/admin-bug-reports.ts';
import { PageActions } from '../components/app/shell/page-slot.tsx';
import { RouteDrawer } from '../components/kit/route-drawer.tsx';
import { useBugReportDrawerHeader } from '../features/admin-bug-reports/detail.tsx';
import { BugReportQueue, BugReportStatusFilter } from '../features/admin-bug-reports/queue.tsx';

const searchSchema = z.object({
  status: z.enum(['new', 'seeing', 'resolved', 'all']).default('new'),
});

/** Largura do drawer: o contexto tem tabela de requisições, precisa de mais que o KYC. */
const DRAWER_WIDTH = 560;

function AdminBugReports() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { status } = search;
  const queue = useInfiniteQuery(adminBugReportsQueryOptions(status));
  const rows = queue.data?.pages.flatMap((page) => page.data) ?? [];

  const detail = useMatch({ from: '/admin/bug-reports/$id', shouldThrow: false });
  const header = useBugReportDrawerHeader(detail?.params.id);

  return (
    <>
      <PageActions>
        <BugReportStatusFilter
          value={status}
          onChange={(next) => {
            void navigate({ to: '/admin/bug-reports', search: { status: next } });
          }}
        />
      </PageActions>
      <BugReportQueue
        rows={rows}
        view={status}
        isLoading={queue.isPending}
        error={queue.error}
        onRetry={() => {
          void queue.refetch();
        }}
        hasMore={queue.hasNextPage}
        isLoadingMore={queue.isFetchingNextPage}
        onLoadMore={() => {
          void queue.fetchNextPage();
        }}
        selectedId={detail?.params.id}
        onSelect={(row) => {
          void navigate({ to: '/admin/bug-reports/$id', params: { id: row.id }, search });
        }}
      />

      <RouteDrawer
        open={detail !== undefined}
        onClose={() => {
          void navigate({ to: '/admin/bug-reports', search });
        }}
        title={header.title}
        sub={header.sub}
        width={DRAWER_WIDTH}
      >
        <Outlet />
      </RouteDrawer>
    </>
  );
}

export const Route = createFileRoute('/admin/bug-reports')({
  validateSearch: searchSchema,
  component: AdminBugReports,
});
